/**
 * Portrait lock: a rotated phone shows the app exactly as it was, rather than reflowing.
 *
 * There is no orientation lock to ask for on this platform. `screen.orientation.lock()` is not
 * implemented in Safari at all, and the manifest's `orientation` is ignored in a browser tab —
 * it is declared in `vite.config.ts` for the installed Android case and nothing more. So the
 * shell counter-rotates instead, and this module supplies the one thing CSS cannot work out
 * for itself.
 *
 * Why CSS cannot: a media query sees `orientation: landscape` for *both* landscapes, because
 * the viewport's coordinates are already normalised to whichever way the phone is held. The
 * two differ by 180°, so a fixed counter-rotation would be upright in one and upside-down in
 * the other. `screen.orientation.angle` is the only thing that tells them apart; this writes
 * it to `data-portrait-lock` on `<html>` and `global.css` does the rest.
 */

/** Only phones. An iPad in landscape is taller than this and stays responsive. */
const PHONE_LANDSCAPE = '(orientation: landscape) and (max-height: 600px) and (pointer: coarse)'

/** Re-read cadence while the platform is still reporting a landscape it cannot name. */
const SETTLE_INTERVAL_MS = 100
const SETTLE_ATTEMPTS = 20

/**
 * How far the OS has rotated the viewport from natural — but only when it says something that
 * can be true of a landscape. `undefined` means "not yet", not "portrait".
 *
 * The distinction is the whole defect. An app relaunched straight into landscape reports
 * `angle === 0` for the first moments, and the previous version treated anything unrecognised
 * as 90. That is a coin flip, and when it lost, the app rendered **180° out** — title at the
 * far end from the notch — with no further event to correct it, because no rotation had
 * happened. Waiting costs a few frames of unrotated layout; guessing costs an upside-down app
 * for the whole session.
 */
function viewportAngle(): number | undefined {
  const modern = window.screen.orientation?.angle
  // Safari before 16.4 has only the deprecated `window.orientation` (-90 | 0 | 90 | 180); it
  // is also a useful second opinion when the modern one has not caught up.
  const legacy = (window as { orientation?: number }).orientation
  for (const raw of [modern, legacy]) {
    if (typeof raw !== 'number') continue
    const angle = ((raw % 360) + 360) % 360
    if (angle === 90 || angle === 270) return angle
  }
  return undefined
}

/**
 * Installs the lock and returns its teardown. Safe to call once at startup; the listeners are
 * the three iOS actually fires around a rotation, because no single one of them is reliable.
 */
export function installPortraitLock(): () => void {
  const landscape = window.matchMedia(PHONE_LANDSCAPE)
  let settle: ReturnType<typeof setTimeout> | undefined
  let attempts = 0

  function apply(): void {
    const root = document.documentElement
    clearTimeout(settle)

    if (!landscape.matches) {
      delete root.dataset['portraitLock']
      attempts = 0
      return
    }

    const angle = viewportAngle()
    if (angle === undefined) {
      // Landscape, but the platform has not said which one. Stay unrotated and ask again
      // rather than commit to a guess (see `viewportAngle`).
      delete root.dataset['portraitLock']
      if (attempts < SETTLE_ATTEMPTS) {
        attempts += 1
        settle = setTimeout(apply, SETTLE_INTERVAL_MS)
      }
      return
    }

    attempts = 0
    root.dataset['portraitLock'] = String(angle)
  }

  apply()
  landscape.addEventListener('change', apply)
  window.addEventListener('resize', apply)
  window.screen.orientation?.addEventListener('change', apply)
  // A standalone app is resumed, not reloaded: coming back from the background after a
  // rotation fires neither of the two above in every iOS version.
  window.addEventListener('pageshow', apply)
  document.addEventListener('visibilitychange', apply)

  return () => {
    clearTimeout(settle)
    landscape.removeEventListener('change', apply)
    window.removeEventListener('resize', apply)
    window.screen.orientation?.removeEventListener('change', apply)
    window.removeEventListener('pageshow', apply)
    document.removeEventListener('visibilitychange', apply)
    delete document.documentElement.dataset['portraitLock']
  }
}
